import React, { useCallback, useEffect, useRef, useState } from 'react';
import { prop } from 'ramda';
import { Button } from 'js/components/common/Button';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import {
    usePortletTranslations,
    usePortletConfig,
    usePortletSelector,
    withPortlet
} from 'js/components/system/Portlet';
import { declinePlayerDepositLimits, acceptPlayerDepositLimits, fetchDepositLimit } from './actions';
import { Icon } from 'js/components/common/Icon';
import { useFormatter } from 'js/hooks/useFormatter';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import * as helpers from '../../Limits/helpers';
import { useChangeLimitPopup } from '../ChangeLimitPopup/hooks/useChangeLimitPopup';
import { LAYER } from 'js/components/common/PopupManager';
import { useConfirmationPopup } from 'js/components/common/popups/ConfirmationPopup';
import { debounce } from 'underscore';
import {
    DELAY,
    PERIODS,
    RESPONSIBLE_GAMING_DEPOSIT_LIMITS,
    UNLIMITED_STR
} from '../../Limits/constants';
import style from './index.scss';
import reducer from '../../Limits/reducer';
import { componentService } from 'js/services/component.service';
import { useGetDefultDepositLimits } from './useGetDefaultDepositLimits';
import { dispatcher } from 'dispatcher';

export const getWidthValue = (obj) => {
    const restValue = getIndicatorValue(obj);

    return restValue / obj.amount.amount * 100;
};

export const getIndicatorValue = (limitObject) => {
    const {
        remainingLimit,
        amount
    } = limitObject;
    let result;
    const remaining = Number(remainingLimit.amount).toFixed();

    result = amount.amount - remaining;

    if (result < 0) {
        result = 0;
    } else {
        result = Number(result.toFixed());
    }

    return result;
};

export const DepositLimitsBare = ({
    data: depositLimitsData
}) => {
    const { allowedLimits, waitingLimits, currentLimits } = useGetDefultDepositLimits(depositLimitsData);
    const lang = useUserSelector(prop('lang'));
    const [deStyle, setDeStyle] = useState('');
    const formatter = useFormatter();
    const [isFetchPicker, setFetchedPicker] = useState(false);
    const valuesList = allowedLimits.map(el => Number(el));
    const dayRef = useRef(null);
    const weekRef = useRef(null);
    const monthRef = useRef(null);
    const [leftPositionDay, setLeftPositionDay] = useState('');
    const [leftPositionWeek, setLeftPositionWeek] = useState('');
    const [leftPositionMonth, setLeftPositionMonth] = useState('');
    const showConfirmationPopup = useConfirmationPopup();
    const { portletType } = usePortletConfig();
    const fetchPortlet = usePortletSelector(prop('fetchPortlet'));
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const limitMaxDesktopScreen = dynamicTags && dynamicTags.DTAG_LIMITMAXDESKTOPSCREEN;
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const isBigScreen = document.documentElement.clientWidth > Number(limitMaxDesktopScreen);
    const isDesktopBigScreen = isDesktop && isBigScreen;
    const [isMobile, setMobile] = useState(!isDesktopBigScreen);
    const showChangeLimitPopupAction = useChangeLimitPopup();
    const t = usePortletTranslations();
    const title = t('title.limits');
    const subTitle = t('subtitle.limits');
    const availablePeriods = currentLimits.map(el => el.timePeriod);
    const hasPendingLimits = Object.keys(waitingLimits).length > 0;

    const acceptLimit = useCallback(async (periods) => {
        try {
            await acceptPlayerDepositLimits(periods);
            await fetchPortlet({ type: portletType }, { showLoader: false });
        } catch (error) {
            const { data = {} } = error?.response || error || {};
            console.error(data);
        } finally {
            setTimeout(() => {
                dispatcher.dispatch('quickDeposit:updated');
                setFetchedPicker(true);
            }, DELAY);
        }
    }, [fetchPortlet, acceptPlayerDepositLimits]);

    const applyAccept = () => {
        const isAccepted = waitingLimits.filter(el => el.confirmationNeeded === 1).map(el => el.timePeriod);

        if (isAccepted.length) {
            return acceptLimit(isAccepted);
        }
    };

    const decline = useCallback(async (period) => {
        try {
            await declinePlayerDepositLimits(period);
            await fetchPortlet({ type: portletType }, { showLoader: false });
        } catch (error) {
            const { data = {} } = error?.response || error || {};
            console.error(data);
        } finally {
            dispatcher.dispatch('quickDeposit:updated');
        }
    }, [fetchPortlet, declinePlayerDepositLimits]);

    const deleteDefaultClassMobile = () => {
        if (isMobile) {
            if (document.querySelector('[data-remove]').className) {
                document.querySelectorAll('[data-remove]').forEach(el => el.className = '');
            }
        }
    };

    const deleteLimit = (period) => {
        const getPeriodSection = window.document.querySelectorAll(`[data-interval=${period}]`)[0];
        deleteDefaultClassMobile();

        if (getPeriodSection) {
            if (!isMobile) {
                if (Object.keys(waitingLimits).length === 1) {
                    getPeriodSection.parentElement.dataset.lurk = 'false';
                }

                getPeriodSection.querySelector('[data-waiting]').dataset.waiting = false;
                getPeriodSection.querySelector('[data-leaving]').dataset.leaving = false;
            }
        }

        setTimeout(() => {
            decline([period]);
            if (getPeriodSection && isMobile) {
                getPeriodSection.querySelector('[data-remove]').dataset.remove = Boolean(waitingLimits[period]?.pending);
            }
        }, DELAY);
    };

    const deleteAllLimits = () => {
        const getPeriodSections = window.document.querySelectorAll('[data-interval]');
        deleteDefaultClassMobile();

        if (getPeriodSections.length) {
            if (!isMobile) {
                const getPendingSections = window.document.querySelectorAll('[data-leaving="true"]');
                getPeriodSections[0].parentElement.dataset.lurk = 'false';
                getPendingSections.forEach(el => {
                    el.dataset.leaving = false;
                    el.querySelector('[data-waiting]').dataset.waiting = false;
                });
            }
        }

        setTimeout(() => {
            decline(waitingLimits.map(el => el.timePeriod));
            if (getPeriodSections.length && isMobile) {
                getPeriodSections.forEach(el => {
                    el.querySelector('[data-remove]').dataset.remove = Boolean(waitingLimits[el]?.pending);
                });
            }
        }, DELAY);
    };

    const checkAcceptanceNeededProperty = (timePeriod) => {
        if (waitingLimits.find(el => el.timePeriod === timePeriod) && waitingLimits.find(el => el.timePeriod === timePeriod).hasOwnProperty('confirmationNeeded')) {
            return declineLimit(timePeriod);
        }
    };

    const declineLimit = (period) => {
        const limitValues = [];

        availablePeriods.forEach((el) => {
            const pickLimit = (currentLim) => {
                const isExist = currentLim.filter((key) => key.timePeriod.includes(el));
                if (isExist.length) {
                    return Number(isExist.reduce((obj) => obj).amount.amount);
                }
                    return 0;

            };
            const actualLimit = pickLimit(currentLimits);
            const pendingLim = pickLimit(waitingLimits);

            const pendingLimit = el === period ? actualLimit : pendingLim;
            pendingLimit ? limitValues.push(pendingLimit) : limitValues.push(actualLimit);
        });

        for (let i = 0; i < limitValues.length; i++) {
            if (!(limitValues[i] <= limitValues[i + 1] || limitValues.length === i + 1)) {
                showConfirmationPopup({
                    className: 'confirmation-loss-popup',
                    onAccept: () => deleteAllLimits(),
                    closeAll: true,
                    isModal: false,
                    title: t('rule.title'),
                    content: t('rule.message'),
                    acceptBtnText: 'playtech.mobile.system.continue',
                    declineBtnText: 'playtech.mobile.system.cancel'
                });

                return;
            }
        }

        return deleteLimit(period);
    };

    const setPosition = (elWidthDay, elWidthWeek, elWidthMonth) => {
        document.querySelectorAll('[data-interval]').forEach((el, ind) => {
            if (ind === 0) {
                const valD = helpers.getLeftPosition(currentLimits[ind], elWidthDay);
                setLeftPositionDay(valD);
            }

            if (ind === 1) {
                const valW = helpers.getLeftPosition(currentLimits[ind], elWidthWeek);
                setLeftPositionWeek(valW);
            }

            if (ind === 2) {
                const valM = helpers.getLeftPosition(currentLimits[ind], elWidthMonth);
                setLeftPositionMonth(valM);
            }
        });
    };

    const updatePickers = () => {
        const elDayOffsetWidth = dayRef.current ? dayRef.current.offsetWidth : 0;
        const elWeekOffsetWidth = weekRef.current ? weekRef.current.offsetWidth : 0;
        const elMonthOffsetWidth = monthRef.current ? monthRef.current.offsetWidth : 0;

        return setPosition(elDayOffsetWidth, elWeekOffsetWidth, elMonthOffsetWidth);
    };

    const checkResizing = () => {
        if (isDesktop) {
            if (document.documentElement.clientWidth < Number(limitMaxDesktopScreen)) {
                setMobile(true);
                updatePickers();
            } else {
                setMobile(false);
                updatePickers();
            }
        }
    };

    useEffectOnce(() => {
        if (lang === 'de_DE') {
            setDeStyle('de');
        }
        applyAccept();
        checkResizing();
    }, []);

    useEffect(() => {
        updatePickers();
        setFetchedPicker(false);
    }, [isFetchPicker]);

    useEffect(() => {
        window.addEventListener('orientationchange', debounce(() => updatePickers(), DELAY));

        return () => window.removeEventListener('orientationchange', debounce(() => updatePickers(), DELAY));
    }, [updatePickers]);

    useEffect(() => {
        window.addEventListener('resize', debounce(() => checkResizing(), DELAY));

        return () => window.removeEventListener('resize', debounce(() => checkResizing(), DELAY));
    }, [checkResizing]);

    const reFetchPicker = (rendered) => setFetchedPicker(rendered);

    const formatCurrency = (amount, currency) => {
        const amountNumber = Number(amount);

        return String(amount).length > 4 ?
            formatter.formatCurrency(amountNumber, { currencyCode: currency, symbolAtTheLeft: true, precision: 0, thSeparator: ' ' }) :
            formatter.formatCurrency(amountNumber, { currencyCode: currency, symbolAtTheLeft: true, precision: 0, thSeparator: '' });
    };

    const getDiffDate = (resetDate, isPending) => {
        let date;
        let date2;
        const now = new Date();
        const nowUTC = new Date(now.toUTCString().slice(0, -4));
        if (isPending) {
            const formattedDate = formatter.parseImsDate(resetDate);
            date = Math.abs((new Date().getTime() / 1000).toFixed(0));
            date2 = Math.abs((new Date(formattedDate).getTime() / 1000).toFixed(0));
        } else {
            const formattedDate = formatter.getDateTimeLong(resetDate).replace(/-/g, '/');
            date = Math.abs((new Date(nowUTC).getTime() / 1000).toFixed(0));
            date2 = Math.abs((new Date(formattedDate).getTime() / 1000).toFixed(0));
        }
        const diff = date2 - date;
        const days = Math.floor(diff / 86400);
        const hours = Math.floor(diff / 3600) % 24;
        const minutes = Math.floor(diff / 60) % 60;
        const seconds = diff % 60;
        let daysStr = days;
        let hoursStr = hours;
        let minutesStr = minutes;
        let result;
        const d = t('days');
        const h = t('h');
        const m = t('mins');
        const r = t('remaining');
        const isDay = daysStr ? `${daysStr}${d} ` : ' ';
        const isHour = hoursStr ? `${hoursStr}${h} ` : ' ';
        const isMin = minutesStr ? `${minutesStr}${m} ` : ' ';
        const remaining = isPending ? '' : ` ${r}`;
        const second = `1${m}`;

        if (days < 0 && hours < 0 && minutes < 0 && seconds < 0) {
            daysStr = 0;
            hoursStr = 0;
            minutesStr = 0;
        }

        if (isPending && daysStr === 1 && hoursStr === 0 && minutesStr === 0) {
            result = `23${h} 59${m}`;
        } else {
            if (daysStr) {
                result = isDay + isHour + remaining;
            } else if (!hoursStr && !minutesStr && seconds) {
                result = second + remaining;
            } else {
                result = isHour + isMin + remaining;
            }
        }

        return result;
    };

    const formatRemainingAmount = (amount, code) => {
        const result = Number(amount);

        return String(result).length > 4 ?
            formatter.formatCurrency(result, { currencyCode: code, symbolAtTheLeft: true, precision: 0, thSeparator: ' ' }) :
            formatter.formatCurrency(result, { currencyCode: code, symbolAtTheLeft: true, precision: 0, thSeparator: '' });
    };

    const newActual = currentLimits.map(limit => ({
        ...limit,
        limit: limit.amount,
        timePeriod: PERIODS[availablePeriods.indexOf(limit.timePeriod)]
    })).reduce((acc, limit) => {
        const { timePeriod, amount } = limit;
        const key = 'actualLimits';

        acc[key] = {
            ...acc[key],
            [timePeriod]: {
                timePeriod,
                limit: amount
            }
        };

        return acc;
    }, {});

    const changeLimit = () => {
        const titlePopup = t('popup.title');

        showChangeLimitPopupAction({
            layer: LAYER.ADVANCED_PLAYER_MESSAGE,
            title: titlePopup,
            portletType,
            availablePeriods: Object.keys(newActual.actualLimits),
            actualLimits: newActual.actualLimits,
            valuesLimit: valuesList,
            fetchPortlet,
            reFetchPicker
        });
    };

    return (
        <div className={`${isMobile ? style.swissMobileLimit : style.swissDesktopLimit} cms-deposit-limits`}>
            <div className={deStyle === 'de' ? style.swissDeLimitComponent : style.swissLimitComponent}
                 data-lurk={Boolean(hasPendingLimits)}>
                {title && <h2 className={style.title}>{t('title.limits')}</h2>}
                {subTitle && <h3 className={style.subtitle}>{t('subtitle.limits')}</h3>}

                {availablePeriods.length && availablePeriods.map((timePeriod, index) => (
                    <div key={index}
                         data-interval={timePeriod}
                         className={isMobile ? `${style.limitMobileWrapper}` : `${style.limitWrapper}`}>
                        <div className={style.limitContainer}>
                            <div className={style.limitInnerContainer}>
                                <div className={style.limitActiveContainer}>
                                    {isMobile ? (
                                        <div className={style.mobileTitleContainer}>
                                            <div className={style.titleActive}>{t(`active.${timePeriod}`)}</div>
                                            <div className={style.activeAmountLimit}>
                                                {currentLimits[index]?.amount?.amount === UNLIMITED_STR ?
                                                    t('noLimit') :
                                                    formatCurrency(currentLimits[index]?.amount?.amount,
                                                        currentLimits[index]?.amount?.currencyCode)
                                                }
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={style.titleActive}>{t(`active.${timePeriod}`)}</div>
                                    )}
                                    <div className={style.indicatorContainer}>
                                        <div className={style.progressBarContainer}>
                                            <div className={style.limitProgressBar}>
                                                {currentLimits[index]?.amount?.amount === UNLIMITED_STR ? (
                                                    <div className={style.limitIndicator}
                                                         style={{ width: '0' }}>
                                                    </div>
                                                ) : (
                                                    <div className={style.limitIndicator} data-time={timePeriod}
                                                         style={{ width: `${getWidthValue(currentLimits[index]) > 3 ? getWidthValue(currentLimits[index]) : 3}%` }}>
                                                        <div>
                                                            <figcaption ref={timePeriod === 'daily' ? dayRef : timePeriod === 'weekly' ? weekRef : monthRef} className={`${style.limitIndicatorValue}`} style={{ left: timePeriod === 'daily' ? leftPositionDay : timePeriod === 'weekly' ? leftPositionWeek : leftPositionMonth }}>
                                                                    <span className={style.limitIndicatorValueText}>
                                                                       {formatCurrency(getIndicatorValue(currentLimits[index]),
                                                                           currentLimits[index]?.amount?.currencyCode)}
                                                                    </span>
                                                            </figcaption>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        { !isMobile && <div className={style.activeAmountLimit}>
                                            {currentLimits[index]?.amount?.amount === UNLIMITED_STR ?
                                                t('noLimit') :
                                                formatCurrency(currentLimits[index]?.amount?.amount,
                                                    currentLimits[index]?.amount?.currencyCode)
                                            }
                                        </div>
                                        }
                                    </div>
                                </div>
                                <div className={style.remainingLimitContainer}>
                                    <div className={style.containerText}>
                                        <div className={style.titleRemaining}>
                                            <span>{t('remainingLimit')}</span>
                                        </div>
                                        {currentLimits[index]?.resetDate &&
                                            <div className={style.dateRemaining}>
                                                    <span>
                                                        {getDiffDate(currentLimits[index].resetDate)}
                                                    </span>
                                            </div>
                                        }
                                    </div>
                                    <div className={style.amountRemaining}>
                                            <span className={style.remainingAmountValue}>
                                                {currentLimits[index]?.amount?.amount === UNLIMITED_STR ?
                                                    t('noLimit') :
                                                    formatRemainingAmount(currentLimits[index]?.remainingLimit?.amount,
                                                        currentLimits[index]?.remainingLimit?.currencyCode)
                                                }
                                            </span>
                                    </div>
                                </div>
                                {isMobile &&
                                    <div data-remove={Boolean(waitingLimits.find(el => el.timePeriod === timePeriod)?.amount)}
                                         className={style.firstInitMobilePending}>
                                    <div
                                        className={style.pendingInnerContainer}
                                        onClick={() => checkAcceptanceNeededProperty(timePeriod)}
                                    >
                                        <div className={style.containerText}>
                                            <div className={style.titlePending}>
                                                <span>{t('appliedFor')}</span>
                                                {waitingLimits.find(el =>
                                                    el.timePeriod === timePeriod)?.amount?.amount === UNLIMITED_STR ?
                                                    <span>{` ${t('noLimit')}`}</span> :
                                                    <span>{` ${formatCurrency(waitingLimits.find(el => el.timePeriod === timePeriod)?.amount?.amount, waitingLimits.find(el => el.timePeriod === timePeriod)?.amount?.currencyCode)}`}</span>
                                                }
                                            </div>
                                            <div className={style.pendingDate}>
                                                <span>{t('startingIn')}</span>
                                                {waitingLimits.find(el => el.timePeriod === timePeriod)?.amount &&
                                                    <span>{` ${getDiffDate(waitingLimits.find(el => el.timePeriod === timePeriod).startDate, true)}`}</span>
                                                }
                                            </div>
                                        </div>
                                        <div className={style.amountPending}>
                                            {waitingLimits.find(el => el.timePeriod === timePeriod) && waitingLimits.find(el => el.timePeriod === timePeriod).hasOwnProperty('confirmationNeeded') &&
                                                <Icon data-time={timePeriod}
                                                      className={style.closeMobileLimit}/>
                                            }
                                        </div>
                                    </div>
                                </div>}
                            </div>
                        </div>
                        {!isMobile && <div>
                            <div>
                                <div data-leaving={Boolean(waitingLimits.find(el => el.timePeriod === timePeriod)?.timePeriod)}>
                                    {waitingLimits.find(el => el.timePeriod === timePeriod)?.timePeriod &&
                                        <div className={deStyle === 'de' ? style.pendingLimitContainerDE : style.pendingLimitContainer} data-waiting={true}>
                                            <div className={style.pendingInnerContainer}>
                                                <div className={style.containerText}>
                                                    <div className={style.titlePending}>
                                                        <span>{t('pendingLimit')}</span>
                                                    </div>
                                                    <div className={style.pendingDate}>
                                                        <span>{t('activeIn')}</span>
                                                        <span>{` ${getDiffDate(waitingLimits.find(el => el.timePeriod === timePeriod)?.startDate, true)}`}</span>
                                                    </div>
                                                </div>
                                                <div className={style.amountPending}>
                                                    <span className={style.remainingAmountValue}>
                                                        {waitingLimits.find(el =>
                                                            el.timePeriod === timePeriod)?.amount?.amount === UNLIMITED_STR ?
                                                            t('noLimit') :
                                                            formatCurrency(waitingLimits.find(el =>
                                                                    el.timePeriod === timePeriod)?.amount?.amount,
                                                                waitingLimits.find(el =>
                                                                    el.timePeriod === timePeriod)?.amount?.currencyCode)
                                                        }
                                                    </span>
                                                    {waitingLimits.find(el => el.timePeriod === timePeriod).hasOwnProperty('confirmationNeeded') &&
                                                        <Icon
                                                            name={waitingLimits.find(el =>
                                                                el.timePeriod === timePeriod).timePeriod}
                                                            data-time={timePeriod}
                                                            className={style.deleteIcon}
                                                            onClick={() => declineLimit(timePeriod)}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                ))}
                <Button
                    type="button"
                    className={style.changeLimitBtn}
                    onClick={changeLimit}
                >
                    {t('changeLimit')}
                </Button>
            </div>
        </div>
    );
};

export const DepositLimits = withPortlet({
    reducer,
    forLoggedIn: true,
    fetch: fetchDepositLimit
})(DepositLimitsBare);

componentService.registerPageComponents({
    [RESPONSIBLE_GAMING_DEPOSIT_LIMITS]: DepositLimits
});
